import React, { useEffect, useContext, useState, useRef, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useSWR from 'swr'
import { AppContext } from '@state/context'
import {
  saveSection,
  saveSectionFailed,
  saveSectionSuccess,
  setCurrentEditBookId,
  setCurrentEditBookObject,
  setCurrentSection,
  setEditMode,
  setSelectedBookId,
  setSelectedBookTitle,
} from '@state/actions/appActions'
import { openPanel } from '@state/actions/panelsActions'
import { Single_Book } from '@graphql/queries'
import SideBarPage from '@views/SideBarPage'
import SideBar from '@components/SideBar'
import Button from '@components/Button'
import {
  BUTTON_SIZE,
  BUTTON_TYPE,
  COLORS,
  DIVIDER_SIZE,
  HEADING_TYPE,
  PANELS,
  TEXT_SIZES,
  IMAGE_SIZES,
  CONTENT_TYPES,
  USER_ROLES,
  CONTAINER_STYLE,
  EXAM_DISPLAY_TYPES,
  SCHOOL_RESOURCE_TYPES,
} from '@helpers/constants'
import Heading from '@components/Heading'
import Text from '@components/Text'
import Loader from '@components/Loader'
import Image from '@components/Image'
import Divider from '@components/Divider'
import SubjectHeader from '@components/SubjectHeader'
import ContentHeader from '@components/ContentHeader'
import SectionRenderer from '@components/SectionRenderer'
import { setSubjectFilter } from '@state/actions/filtersActions'
import { useUpdateBook } from '@hooks/mutations/useUpdateBook'
import { getBookImageUrl } from '@helpers/book'
import ButtonWithLoader from '@components/ButtonWithLoader'
import request from 'graphql-request'
import { Update_Section } from '@graphql/mutations'
import Editor from '@components/Editor'
import Exams from '@components/Exams'
import QuizExamView from '@components/Exams/QuizExamView'
import Container from '@components/Container'
import SingleBookInfoCard from '@components/SingleBookInfoCard'

import { useSchoolClass } from '@hooks/useSchoolClass'
import { useSchoolGroup } from '@hooks/useSchoolGroup'
import { useExamsAllowed } from '@hooks/useExamsAllowed'
import { useSchoolClassesOptions } from '@hooks/useSchoolClassesOptions'
import { useSchoolGroupsOptions } from '@hooks/useSchoolGroupsOptions'
import SelectButton from '@components/SelectButton'
import { replaceBreadcrumbAtIndex } from '@state/actions/breadcrumbsActions'
import SectionEnderFeedback from '@components/SectionEnderFeedback'
import { getFirstUncompletedSectionIndex } from '@helpers/exams'

const SingleBook = (props) => {
  const { resourceType } = props
  const { resourceId, id } = useParams()

  const [state, dispatch] = useContext(AppContext)
  const editorRef = useRef()
  const onSaveCallbackRef = useRef()
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const {
    app: {
      currentEditBookObject,
      userRole,
      userId,
      currentSection,
      editMode,
      currentActiveExam,
    },

    editor: { onSaveCallbacks },
    breadcrumbs: { breadcrumbs },
  } = state

  const examsAllowed = useExamsAllowed()

  let resource
  let bookOnResource
  // let schoolClassesOptions
  // let schoolGroupsOptions

  if (
    examsAllowed &&
    resourceId &&
    resourceType &&
    userRole &&
    userRole !== USER_ROLES.STUDENT
  ) {
    const { schoolClass, error: schoolClassError } = useSchoolClass(resourceId)
    const { schoolGroup, error: schoolGroupError } = useSchoolGroup(resourceId)

    if (resourceType === SCHOOL_RESOURCE_TYPES.CLASS) {
      resource = schoolClass
    }

    if (resourceType === SCHOOL_RESOURCE_TYPES.GROUP) {
      resource = schoolGroup
    }

    // const { schoolClassesOptions: scOptions } = useSchoolClassesOptions()
    // schoolClassesOptions = scOptions
    // const { schoolGroupsOptions: sgOptions } = useSchoolGroupsOptions()
    // schoolGroupsOptions = sgOptions
  }

  if (resource) {
    bookOnResource = resource.books.find(
      (rb) => rb.id === currentEditBookObject?.id
    )
  }

  const breadcrumb = useMemo(() => {
    if (currentEditBookObject?.id === id) {
      return {
        label: currentEditBookObject?.name ?? '',
      }
    }

    return null
  }, [currentEditBookObject, id])

  // Fix for update breadcrumb when teacher switching between classes/groups in dropdowns
  // useEffect(() => {
  //   if (resource && resourceType && breadcrumbs.length === 3) {
  //     const crumbLink = breadcrumbs[1].link
  //     const match = crumbLink.match(/\/(klass|grupp)\/(\d+)/)
  //     const crumbLinkId = match ? match[2] : null

  //     if (crumbLinkId !== resource.id) {
  //       const newBreadcrumb = { link: '', label: resource.name }
  //       if (resourceType === SCHOOL_RESOURCE_TYPES.CLASS) {
  //         newBreadcrumb['link'] = `/klass/${resource.id}`
  //       }

  //       if (resourceType === SCHOOL_RESOURCE_TYPES.GROUP) {
  //         newBreadcrumb['link'] = `/grupp/${resource.id}`
  //       }

  //       dispatch(
  //         replaceBreadcrumbAtIndex({ index: 1, newBreadcrumb: newBreadcrumb })
  //       )
  //     }
  //   }
  // }, [breadcrumbs, resource, resourceType])

  const imageUrl = getBookImageUrl(currentEditBookObject)

  useEffect(() => {
    onSaveCallbackRef.current = onSaveCallbacks
  }, [onSaveCallbacks])

  useEffect(() => {
    if (currentEditBookObject) {
      let startSectionIndex = 0

      if (examsAllowed && userRole === USER_ROLES.STUDENT) {
        startSectionIndex = getFirstUncompletedSectionIndex(
          currentEditBookObject.sections
        )
      }

      dispatch(
        setCurrentSection(currentEditBookObject.sections[startSectionIndex])
      )
    }

    return () => dispatch(setCurrentSection(null))
  }, [currentEditBookObject?.id, userRole, examsAllowed])

  const { data, error } = useSWR([Single_Book, id])

  useEffect(() => {
    if (data && !error) {
      const book = data?.book

      if (!book) {
        navigate('/401', { replace: true })
        return
      }

      dispatch(setCurrentEditBookObject(book))
      dispatch(setCurrentEditBookId(book.id))

      // Update currentSection if book has matching section (to trigger update when editing in section-panel from inside book)
      let updatedCurrentSection = book?.sections?.find((section) => {
        return currentSection && currentSection.id === section.id
      })

      if (updatedCurrentSection) {
        dispatch(setCurrentSection(updatedCurrentSection))
      }
    }

    // TODO, cannot clear on unmount here, since it clears bookObject in state, and its beeing used by EditSection in EditBookPanel
    // return () => dispatch(setCurrentEditBookObject(null))
  }, [data, error])

  const isSaved = currentEditBookObject?.customers.some(
    (customer) => customer?.id === userId
  )
  const { post, isPosting } = useUpdateBook()
  const updateBook = async (connectCustomers = []) => {
    await post(
      {
        id: currentEditBookObject?.id,
        connectCustomers: connectCustomers,
        disconnectCustomers: [],
      },
      'Sparad i bokhyllan.',
      'Nåt gick fel när boken skulle sparas i bokhyllan.'
    )
  }

  const save = async () => {
    setIsLoading(true)

    await Promise.all(
      Object.values(onSaveCallbackRef.current).map(
        async (callback) => await callback()
      )
    )

    dispatch(saveSection())
    try {
      const currentContent = await editorRef.current.getContent()

      if (currentContent) {
        const currentContentStr = JSON.stringify(currentContent)

        const variables = {
          id: currentSection.id,
          content: currentContentStr,
          teachersGuideContent: currentSection.teachersGuideContent,
        }

        const res = await request('/graphql', Update_Section, variables)

        if (res?.updateSection?.id) {
          // Section update success
          console.log('🚀 ~ Section update success, id:', res.updateSection.id)
          dispatch(saveSectionSuccess())
        } else {
          console.log('🚀 ~ could not update')
          dispatch(saveSectionFailed())
        }
      } else {
        console.log('🚀 ~ editor not ready for save')
        dispatch(saveSectionFailed())
      }
    } catch (error) {
      console.log('🚀 ~ error saving', error)
      dispatch(saveSectionFailed())
    }
    setIsLoading(false)
  }

  const switchToPreviewMode = async () => {
    dispatch(setEditMode(false))
    const currentContent = await editorRef.current.getContent()
    const updatedSection = {
      ...currentSection,
      content: currentContent,
    }

    dispatch(setCurrentSection(updatedSection))
    setIsLoading(false)
  }

  // Quiz Exam in Fullscreen
  if (
    currentActiveExam &&
    currentActiveExam.displayType ===
      parseInt(EXAM_DISPLAY_TYPES.FULL_SCREEN.value)
  ) {
    return <QuizExamView fullScreen />
  }

  return (
    <SideBarPage>
      {/* sidebar */}
      <SideBar breadcrumb={breadcrumb}>
        {!data && !error && <Loader />}
        {error && <p>Något blev fel när boken skulle laddas</p>}

        {data &&
          !error &&
          currentEditBookObject &&
          currentEditBookObject?.subject && (
            <div>
              <Divider size={DIVIDER_SIZE.SMALL} />

              {/* {resource && resourceType && (
                <>
                  {schoolClassesOptions?.length > 0 && (
                    <SelectButton
                      onChange={(e) => {
                        if (e.target.value) {
                          const url = `${e.target.value}/bok/${currentEditBookObject.id}`
                          navigate(url)
                        }
                      }}
                      name={'schoolClass'}
                      id={'schoolClass'}
                      options={[
                        {
                          value: '',
                          name: 'Välj klass',
                        },
                        ...schoolClassesOptions,
                      ]}
                      // defaultValue={`/klass/${resource.id}`}
                      defaultValue={``}
                      value={
                        resourceType === SCHOOL_RESOURCE_TYPES.CLASS
                          ? `/klass/${resource.id}`
                          : ''
                      }
                      extraClasses={'max-w-[200px]'}
                    />
                  )}
                  {schoolGroupsOptions?.length > 0 && (
                    <SelectButton
                      onChange={(e) => {
                        if (e.target.value) {
                          const url = `${e.target.value}/bok/${currentEditBookObject.id}`
                          navigate(url)
                        }
                      }}
                      name={'schoolGroup'}
                      id={'schoolGroup'}
                      options={[
                        {
                          value: '',
                          name: 'Välj grupp',
                        },
                        ...schoolGroupsOptions,
                      ]}
                      // defaultValue={`/grupp/${resource.id}`}
                      defaultValue={``}
                      value={
                        resourceType === SCHOOL_RESOURCE_TYPES.GROUP
                          ? `/grupp/${resource.id}`
                          : ''
                      }
                      extraClasses={'max-w-[200px]'}
                    />
                  )}
                  <Divider size={DIVIDER_SIZE.SMALL} />
                </>
              )} */}

              <SingleBookInfoCard
                imageUrl={imageUrl}
                book={bookOnResource ?? currentEditBookObject}
                resource={resource}
              />

              <Divider size={DIVIDER_SIZE.SMALL} />

              {userRole !== USER_ROLES.STUDENT && (
                <div>
                  <Button
                    icon={'Information'}
                    iconLeft
                    type={BUTTON_TYPE.MEDIUM_DARK_GREY}
                    size={BUTTON_SIZE.MEDIUM}
                    handleClick={() => {
                      dispatch(setSelectedBookId(id))
                      dispatch(openPanel(PANELS.VIEW_BOOK))
                    }}
                  >
                    Information om boken
                  </Button>

                  <Divider size={DIVIDER_SIZE.SMALL} />
                </div>
              )}

              <Divider
                size={DIVIDER_SIZE.LINE}
                color={COLORS.LIGHT_DARK_GREY.BORDER}
                line
              />
            </div>
          )}

        {data && !error && currentEditBookObject && (
          <>
            <SideBar.SectionList
              subject={currentEditBookObject.subject}
              sections={currentEditBookObject.sections}
              currentSection={currentSection}
              onClick={(section) => {
                if (!editMode) {
                  dispatch(setCurrentSection(section))
                }
              }}
              disabled={editMode}
              sectionsOnResource={
                bookOnResource ? bookOnResource.sections : undefined
              }
              resource={resource}
              resourceType={resourceType}
            />

            <Divider
              size={DIVIDER_SIZE.LINE}
              color={COLORS.LIGHT_DARK_GREY.BORDER}
              line
            />

            <Divider size={DIVIDER_SIZE.SMALL} />

            {!resourceType && userRole !== USER_ROLES.STUDENT && (
              <div className={'mb-4'}>
                {userId !== currentEditBookObject?.creator?.id ? (
                  <Button
                    type={BUTTON_TYPE.MEDIUM_DARK_GREY}
                    size={BUTTON_SIZE.LARGE}
                    disabled={isPosting || isSaved}
                    handleClick={() => updateBook([userId])}
                    icon={'Favourite'}
                    iconRight
                  >
                    {isSaved ? 'Sparad i min bokhylla' : 'Spara i min bokhylla'}
                  </Button>
                ) : (
                  <Button
                    type={BUTTON_TYPE.PRIMARY}
                    size={BUTTON_SIZE.LARGE}
                    handleClick={() => {
                      dispatch(openPanel(PANELS.EDIT_BOOK))
                    }}
                  >
                    Redigera bok
                  </Button>
                )}
              </div>
            )}

            {!resourceType &&
              userId === currentSection?.creator?.id &&
              !editMode && (
                <div className='mb-4'>
                  <Button
                    handleClick={() => {
                      dispatch(setEditMode(true))
                    }}
                    textColor={COLORS.WHITE.TEXT}
                    backgroundColor={COLORS.PRIMARY_GREEN.BACKGROUND}
                    type={BUTTON_TYPE.PRIMARY}
                    size={BUTTON_SIZE.LARGE}
                    icon={'Pencil'}
                    iconLeft
                  >
                    Redigera avsnitt
                  </Button>
                </div>
              )}

            {editMode && (
              <div className={'flex items-center mb-4'}>
                <div className={'mr-4'}>
                  <ButtonWithLoader
                    handleClick={save}
                    enabledText={'Spara'}
                    isLoading={isLoading}
                    type={BUTTON_TYPE.PRIMARY}
                    size={BUTTON_SIZE.LARGE}
                    icon={'ClipboardAdd'}
                    iconLeft
                  />
                </div>
                <div className={'mr-4'}>
                  <Button
                    handleClick={switchToPreviewMode}
                    textColor={COLORS.WHITE.TEXT}
                    backgroundColor={COLORS.PRIMARY_GREEN.BACKGROUND}
                    type={BUTTON_TYPE.PRIMARY}
                    size={BUTTON_SIZE.LARGE}
                    iconLeft
                    icon={'Eye'}
                  >
                    Förhandsgranska
                  </Button>
                </div>
              </div>
            )}

            {userRole === USER_ROLES.TEACHER && (
              <div className={'mb-4'}>
                <Button
                  type={BUTTON_TYPE.MEDIUM_DARK_GREY}
                  size={BUTTON_SIZE.LARGE}
                  handleClick={() => {
                    dispatch(setSelectedBookId(currentEditBookObject.id))
                    dispatch(setSelectedBookTitle(currentEditBookObject.name))
                    dispatch(openPanel(PANELS.SHARE_BOOK))
                  }}
                  icon={'Share'}
                  iconRight
                >
                  Dela bok med klass
                </Button>
              </div>
            )}

            {/* <div className={'mb-4'}>
              <Button
                type={BUTTON_TYPE.PRIMARY}
                size={BUTTON_SIZE.LARGE}
                handleClick={() => {
                  dispatch(
                    openFeedbackPanel({
                      type: 'book',
                      id: currentEditBookObject.id,
                    })
                  )
                }}
                icon={'Comment'}
                iconRight
              >
                Tyck till om boken
              </Button>
            </div> */}
          </>
        )}
      </SideBar>

      {/* main area */}
      <SideBarPage.Content>
        {!data && !error && <Loader />}
        {error && <Text>Nåt gick fel när vi skulle ladda boken</Text>}

        {data &&
          !error &&
          currentEditBookObject &&
          currentSection &&
          currentSection.subject && (
            <>
              <ContentHeader hideButtons={userRole === USER_ROLES.STUDENT} />

              {editMode && (
                <>
                  <div
                    className={
                      'flex flex-col items-start my-0 mx-auto max-w-[700px]'
                    }
                  >
                    <Button
                      size={BUTTON_SIZE.NO_PADDING}
                      handleClick={() => {
                        dispatch(openPanel(PANELS.EDIT_SECTION))
                      }}
                      icon={'Pencil'}
                      iconRight
                    >
                      <SubjectHeader subject={currentSection?.subject} dark />
                    </Button>

                    <Button
                      icon={'Pencil'}
                      iconRight
                      size={'noPadding'}
                      handleClick={() => {
                        dispatch(openPanel(PANELS.EDIT_SECTION))
                      }}
                    >
                      <Heading
                        headingLevel={HEADING_TYPE.H1}
                        size={TEXT_SIZES.XXXLARGE}
                      >
                        {currentSection.name}
                      </Heading>
                    </Button>
                  </div>

                  <div className='max-w-[700px] mx-auto'>
                    <Editor
                      ref={editorRef}
                      content={
                        typeof currentSection?.content === 'string'
                          ? JSON.parse(currentSection.content)
                          : currentSection.content
                      }
                      onSave={save}
                      elementId={'editorjs'}
                    />
                  </div>
                </>
              )}

              {!editMode && <SectionRenderer section={currentSection} />}

              {/* Exams */}
              <Exams section={currentSection} editMode={editMode} />

              {/* Feedback */}
              {!editMode && <SectionEnderFeedback section={currentSection} />}
            </>
          )}

        {data &&
          !error &&
          currentEditBookObject &&
          !currentEditBookObject.sections.length && (
            <div className='mx-auto mt-[25%] max-w-[310px] text-center'>
              {userRole === USER_ROLES.STUDENT && (
                <Heading
                  headingLevel={HEADING_TYPE.H2}
                  size={TEXT_SIZES.XLARGE}
                >
                  Inga avsnitt finns i boken än
                </Heading>
              )}

              {userRole !== USER_ROLES.STUDENT && (
                <>
                  <Heading
                    headingLevel={HEADING_TYPE.H2}
                    size={TEXT_SIZES.XLARGE}
                  >
                    Välj avsnitt att lägga till i din bok
                  </Heading>

                  <Divider size={DIVIDER_SIZE.SMALL} />

                  <Button
                    type={BUTTON_TYPE.PRIMARY}
                    size={BUTTON_SIZE.LARGE}
                    iconLeft
                    icon={'Section'}
                    to={'/bibliotek/avsnitt'}
                    handleClick={() => {
                      dispatch(
                        setSubjectFilter(currentEditBookObject.subject.id)
                      )
                    }}
                  >
                    Välj avsnitt
                  </Button>
                </>
              )}
            </div>
          )}
      </SideBarPage.Content>
    </SideBarPage>
  )
}

export default SingleBook
